import React, { useEffect, useState, useRef, useCallback } from "react";
import { string, bool, arrayOf, shape, objectOf } from "prop-types";
import cx from "classnames";
import debounce from "lodash.debounce";

import Pagination from "../../components/Pagination/Pagination.jsx";
import FilterBar from "../../components/FilterBar/FilterBar.jsx";
import SelectedFilters from "../../components/SearchParts/SelectedFilters/SelectedFilters.jsx";
import Sorters from "../../components/Sorters/Sorters.jsx";
import ResultsInfo from "../../components/ResultsInfo/ResultsInfo.jsx";
import ManualContentListing from "../../components/ManualContentListing/ManualContentListing.jsx";
import SimpleSearchBox from "../../components/SimpleSearchBox/SimpleSearchBox.jsx";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner.jsx";

import initialState from "./initialState.js";
import fetchSearchResults from "../../common/fetchSearchResults.js";

import {
  toggleFacet,
  clearFacetGroup,
  clearFacet,
  initFacets,
  initCurrentParams,
  createQueryString,
} from "../../common/dynamicSearch.js";

const propTypes = {
  title: string,
  enableQueryString: bool,
  pagination: bool,
  hideMobileSummary: bool,
  url: string.isRequired,
  dictionary: objectOf(string).isRequired,
  query: shape({
    listingId: string,
    showSummaries: bool,
    showDates: bool,
    showContentTypes: bool,
    showImages: string,
    loadAllPages: bool,
    pageId: string,
  }).isRequired,
  sorters: arrayOf(
    shape({
      direction: string,
      label: string,
      value: string,
      isActive: bool,
      id: string,
    }),
  ),
};

const Search = (props) => {
  const {
    title,
    enableQueryString,
    pagination,
    hideMobileSummary,
    url,
    dictionary,
    query,
    sorters,
  } = props;

  const generalParams = {
    ...initialState.query,
    ...query,
  };

  const [showOverlay, setShowOverlay] = useState(false);

  //initialize the facets present in the query string
  const [selectedFacets, setSelectedFacets] = useState(
    initFacets(window.location.search),
  );

  const [serverData, setServerData] = useState(null);

  //initialize the query to that of the query string if it exists
  const [currentParams, setCurrentParams] = useState(
    initCurrentParams(window.location.search, generalParams),
  );

  //update the url with the latest query parameters
  useEffect(() => {
    if (enableQueryString) {
      window.history.replaceState(
        "",
        "",
        window.location.origin +
          window.location.pathname +
          "?" +
          createQueryString(currentParams, selectedFacets),
      );
    }
  }, [currentParams, enableQueryString, selectedFacets]);

  const handleQueryChange = debounce((term) => {
    setCurrentParams((prevState) => {
      return { ...prevState, q: term, page: 1 };
    });
  }, 500);

  const resultsFocusRef = useRef();
  const showFocusRef = useRef({ showFocus: false });

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    setShowOverlay(true);

    fetchSearchResults(
      url,
      {
        ...currentParams,
        ...selectedFacets,
      },
      "default",
      signal,
    )
      .then(function (data) {
        if (data) {
          setServerData(data);
        }
      })
      .catch(function (e) {
        if (e.name === "AbortError") {
          return; // Continuation logic has already been skipped, so return normally
        }
        console.warn("request error: " + e.message);
      })
      .finally(function () {
        setShowOverlay(false);
      });
    // unmounts or data changes
    return () => {
      controller.abort();
    };
  }, [currentParams, selectedFacets, url]);

  useEffect(() => {
    if (resultsFocusRef.current && showFocusRef.current.showFocus) {
      //move the focus back to the top of the results on page change
      resultsFocusRef.current.focus();
      showFocusRef.current.showFocus = false;
    }
  }, [serverData]);

  const handlePaginationChange = (page) => {
    showFocusRef.current.showFocus = true;
    setCurrentParams((prevState) => {
      return { ...prevState, page: page };
    });
  };

  const handleFacetChange = useCallback((facetGroupValue, facet) => {
    setCurrentParams((prevState) => {
      return {
        ...prevState,
        page: 1,
      };
    });

    setSelectedFacets((prevState) => {
      return toggleFacet(facetGroupValue, facet, prevState);
    });
  }, []);

  const handleClearFacetGroup = (facetGroupValue) => {
    setSelectedFacets((prevState) => {
      return clearFacetGroup(facetGroupValue, prevState);
    });
  };

  //clear a single facet
  const clearSelectedFacet = (facetGroupValue, facetId) => {
    setSelectedFacets((prevState) => {
      return clearFacet(facetGroupValue, facetId, prevState);
    });
  };

  //clear all facets regardless of group
  const clearAllFacets = () => {
    setSelectedFacets({});
  };

  const handleSorterClick = (params) => {
    setCurrentParams((prevState) => {
      return { ...prevState, ...params, page: 1 };
    });
  };

  return (
    <div className="search">
      <div className="search__search-box">
        <div className="container container--100">
          {title && <h2 className="search__title">{title}</h2>}
          <SimpleSearchBox
            searchTerm={currentParams.q}
            onTextChange={handleQueryChange}
            placeholder={dictionary.searchPlaceholder}
          />
        </div>
      </div>

      <div className="container">
        {serverData && serverData.facets && (
          <div className="search__filter-bar">
            {dictionary.filterTitle && (
              <div className="search__filter-title">
                {dictionary.filterTitle}
              </div>
            )}
            <FilterBar
              filterTitle={dictionary.filterTitle}
              filterLabel={dictionary.filterLabel}
              onFacetChange={handleFacetChange}
              facets={serverData.facets}
              onClearFacetGroup={handleClearFacetGroup}
              selectedFacets={selectedFacets}
            />
          </div>
        )}
        {selectedFacets && JSON.stringify(selectedFacets) !== "{}" && (
          <div className="search__selected-filters">
            <SelectedFilters
              label={dictionary?.selectedFilters}
              clearLabel={dictionary?.clearAll}
              facets={serverData?.facets}
              onClearItem={handleFacetChange}
              onClearAll={clearAllFacets}
            />
          </div>
        )}

        {serverData && serverData.request && serverData.totalResults >= 0 && (
          <div className="search__search-results-info">
            <ResultsInfo
              totalResults={serverData.totalResults}
              request={serverData.request}
              resultsInfoLabel={dictionary.resultsInfoLabel}
              query={currentParams.q}
            />
          </div>
        )}
        {sorters && serverData && serverData.totalResults > 0 && (
          <div className="search__sorters">
            <Sorters
              onSorterClick={handleSorterClick}
              sorters={sorters}
              sortBy={currentParams.sortBy}
              sortDirection={currentParams.sortOrder}
            />
          </div>
        )}
        {serverData && serverData.results && (
          <div
            className={cx("search__results", {
              "search__results--no-mobile-summaries": hideMobileSummary,
            })}
          >
            {showOverlay && <LoadingSpinner />}
            <ManualContentListing
              updatedLabel={dictionary.updatedLabel}
              results={serverData.results}
              focusRef={resultsFocusRef}
            />
          </div>
        )}
        {pagination &&
          !query.loadAllPages &&
          serverData &&
          serverData.totalResults > 0 && (
            <div className="search__pagination search__pagination--centered">
              <Pagination
                totalResults={serverData.totalResults}
                //this is currently returned as a string from the server, so convert to number
                resultsPerPage={Number(serverData.request.perPage)}
                currentPage={currentParams.page}
                onPaginationChange={handlePaginationChange}
              />
            </div>
          )}
        {query.loadAllPages && serverData && serverData.totalResults > 0 && (
          <div className="search__load-more">
            <button
              onClick={() => handlePaginationChange(currentParams.page + 1)}
              className="btn btn--primary search__load-more-btn"
              type="button"
            >
              {dictionary.loadMoreLabel
                ? dictionary.loadMoreLabel
                : "Load more results"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

Search.propTypes = propTypes;
export default Search;
